import {
  Box,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text
} from '@chakra-ui/react';
import { SearchInput } from '@morressier/alembic';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReviewerSearchAutoSuggestValues } from 'src/api/queries';

export const MIN_AUTOSUGGEST_CHARACTERS = 3;
export const AUTOSUGGEST_DEBOUNCE_MS = 300;

// Escapes Lucene special characters so the backend treats them as literals.
// The original value is preserved for display/storage; only the escaped copy
// is passed to API calls.
export const escapeLuceneSpecialChars = (value: string): string =>
  value.replace(/([+\-&|!(){}[\]^"~*?:\\/])/g, '\\$1');

interface Props {
  value: string;
  onChange: (val: string) => void;
  ctxId: string;
  projectId: string;
  propertyId: string;
  isInvalid?: boolean;
}

const AutoSuggest = ({
  value, // fully controlled — always reflects form state
  onChange,
  ctxId,
  projectId,
  propertyId,
  isInvalid
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value.trim());
    }, AUTOSUGGEST_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [value]);

  const trimmedValue = value.trim();
  const hasMetMinimumLength =
    debouncedValue.length >= MIN_AUTOSUGGEST_CHARACTERS;

  const { data, isFetching } = useQuery({
    queryKey: ['reviewer-autosuggest', propertyId, debouncedValue],
    queryFn: () =>
      fetchReviewerSearchAutoSuggestValues({
        ctxId,
        projectId,
        schemaID: propertyId,
        value: escapeLuceneSpecialChars(debouncedValue)
      }),
    enabled: !!propertyId && hasMetMinimumLength
  });

  const suggestions = data?.items || [];

  const handleChange = (val: string) => {
    onChange(val);
    setIsOpen(val.trim().length > 0);
  };

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      matchWidth
      closeOnBlur={false}
      autoFocus={false}
    >
      <PopoverTrigger>
        <Box width="100%" minW={0}>
          <SearchInput
            width="100%"
            ref={inputRef}
            name="autosuggest-input"
            value={value}
            onChange={e => handleChange(e.target.value)}
            onInputClear={() => handleChange('')}
            onFocus={() => setIsOpen(trimmedValue.length > 0)}
            onBlur={() => setTimeout(() => setIsOpen(false), 150)}
            placeholder={`Type at least ${MIN_AUTOSUGGEST_CHARACTERS} characters to search suggestions`}
            autoComplete="off"
            isInvalid={isInvalid}
          />
        </Box>
      </PopoverTrigger>

      <PopoverContent
        minW="100%"
        zIndex={2000}
        border="1px solid"
        borderColor="neutralPrimaryBorder"
        borderRadius="md"
        boxShadow="md"
        maxH="200px"
        overflowY="auto"
      >
        <PopoverBody padding={0}>
          {!trimmedValue ? (
            <Box px={3} py={2}>
              <Text>Start typing to search suggestions.</Text>
            </Box>
          ) : trimmedValue.length < MIN_AUTOSUGGEST_CHARACTERS ? (
            <Box px={3} py={2}>
              <Text>
                Type at least {MIN_AUTOSUGGEST_CHARACTERS} characters to see
                suggestions.
              </Text>
            </Box>
          ) : isFetching ? (
            <Box p={2} textAlign="center">
              <Spinner size="sm" />
            </Box>
          ) : (
            <List>
              {suggestions.map((item: string) => (
                <ListItem
                  key={item}
                  px={3}
                  py={2}
                  _hover={{ bg: 'actionSecondaryFill', cursor: 'pointer' }}
                  onMouseDown={() => handleSelect(item)}
                >
                  <Text>{item}</Text>
                </ListItem>
              ))}
              {suggestions.length === 0 && (
                <Box px={3} py={2}>
                  <Text>No matches found.</Text>
                </Box>
              )}
            </List>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default AutoSuggest;
